import { ReactNode, useEffect, useMemo, useState } from 'react';
import { View, ActivityIndicator } from 'react-native';
import {
  DesktopReturnContext,
  DesktopReturnButton,
  type DesktopReturn,
} from './screen-frame-slot';
import type { Href } from 'expo-router';
import { useRouter } from 'expo-router';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { WebAppShell, WebContentContainer } from './web/WebAppShell';
import { useAuth } from '@/lib/supabase';
import { mode } from '@/lib/config/app-mode';

export interface Breadcrumb {
  label: string;
  href?: Href;
}

interface AppLayoutProps {
  children: ReactNode;
  title?: string;
  showSearch?: boolean;
  headerRight?: ReactNode;
  /** If true, wraps content in WebContentContainer on web (applies max-width) */
  constrainWidth?: boolean;
  /** Breadcrumbs for navigation hierarchy */
  breadcrumbs?: Breadcrumb[];
}

/**
 * Platform-aware app layout wrapper
 *
 * On mobile: Renders children directly (uses native tab navigation)
 * On web desktop: Wraps in WebAppShell with sidebar
 * On web tablet: WebAppShell with overlay menu
 */
export function AppLayout({
  children,
  title,
  showSearch = false,
  headerRight,
  constrainWidth = true,
  breadcrumbs,
}: AppLayoutProps) {
  const { isDesktop, isTablet } = useResponsive();
  const { user, isLoading } = useAuth();
  const router = useRouter();
  const hasAuth = !!user || mode.isMock;

  // Per-AppLayout, so one screen's return can never leak into another's header.
  const [desktopReturn, setDesktopReturn] = useState<DesktopReturn | null>(null);
  const slot = useMemo(() => ({ setReturn: setDesktopReturn }), []);

  // Auth guard — redirect unauthenticated users to landing page
  // In mock/demo-bypass mode, user may be null but the app is still "authenticated"
  useEffect(() => {
    if (!isLoading && !hasAuth) {
      router.replace('/');
    }
  }, [isLoading, hasAuth, router]);

  // Show loading while checking auth (skip in mock mode — no session to wait for)
  if (isLoading && !mode.isMock) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // Not authenticated — will redirect, render nothing
  if (!hasAuth) {
    return null;
  }

  // Mobile or native: render directly. The provider still wraps, so a
  // ScreenFrame below can register unconditionally without branching on shell.
  if (!isWeb || (!isDesktop && !isTablet)) {
    return (
      <DesktopReturnContext.Provider value={slot}>
        {children}
      </DesktopReturnContext.Provider>
    );
  }

  // Web desktop/tablet: wrap with shell
  return (
    <DesktopReturnContext.Provider value={slot}>
      <WebAppShell
        title={title}
        showSearch={showSearch}
        headerRight={
          desktopReturn || headerRight ? (
            <View className="flex-row items-center gap-3">
              {desktopReturn && <DesktopReturnButton value={desktopReturn} />}
              {headerRight}
            </View>
          ) : undefined
        }
        breadcrumbs={breadcrumbs}
      >
        {constrainWidth ? (
          <WebContentContainer>{children}</WebContentContainer>
        ) : (
          children
        )}
      </WebAppShell>
    </DesktopReturnContext.Provider>
  );
}
